// src/theme/theme.js
import { createTheme, responsiveFontSizes, lighten, alpha } from '@mui/material/styles';

// Define the core primary color
const primaryColor = '#007AFF';
const lightBlueForGradientStart = lighten(primaryColor, 0.75); // Lighter start for gradient ~ #AECBFA or lighter like #D6E6FF
const lightBlueForGradientEnd = lighten(primaryColor, 0.5); // Slightly darker end, but still light ~ #66B2FF or similar

let theme = createTheme({
  palette: {
    primary: {
      main: primaryColor,
      light: lightBlueForGradientEnd, // Use this for general light variations if needed elsewhere
      dark: '#0056b3',
    },
    secondary: {
      main: '#6c757d', // Using grey for secondary actions/text
    },
    background: {
      default: '#FFFFFF', // White background for the left side
      paper: '#FFFFFF', // White background for the card
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
    },
    // Custom gradient definition (can be accessed via theme.palette.gradient)
    gradient: {
       start: lightBlueForGradientStart, // Very light blue
       end: lightBlueForGradientEnd,    // Light-Mid blue
    },
  },
  typography: {
    // fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    fontFamily: "'Inter', sans-serif",
    h4: { // Welcome Title
      fontWeight: 600,
      fontSize: '2.1rem',
      color: primaryColor, // Use primary color for the main title
      lineHeight: 1.3,
    },
    h5: { // Login Card Title
      fontWeight: 600,
      fontSize: '1.5rem',
      color: '#343a40', // Slightly darker than default text for emphasis
      lineHeight: 1.4,
    },
    body1: { // Welcome paragraph
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#6c757d', // text.secondary
    },
    body2: { // Card subtitle, links etc.
      fontWeight: 400,
      fontSize: '0.875rem', // Slightly smaller body text
      lineHeight: 1.5,
      color: '#6c757d', // text.secondary
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.9rem',
    },
  },
  shape: {
    borderRadius: 16, // Consistent, softer border radius for the card
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
          }
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
          }
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.14)',
          }
        },
        elevation4: {
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
          '&:hover': {
            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.16)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
        },
        containedPrimary: {
          background: `linear-gradient(145deg, ${primaryColor} 0%, ${lighten(primaryColor, 0.1)} 100%)`,
          boxShadow: `0 4px 12px ${alpha(primaryColor, 0.3)}`,
          '&:hover': {
            boxShadow: `0 6px 16px ${alpha(primaryColor, 0.35)}`,
            background: `linear-gradient(145deg, ${lighten(primaryColor, 0.1)} 0%, ${primaryColor} 100%)`,
          }
        }
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#dee2e6',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#adb5bd',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            color: '#6c757d'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: primaryColor
          }
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: primaryColor,
          textDecoration: 'none',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline',
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#ced4da',
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardError: {
          backgroundColor: '#fdeded',
          color: '#b71c1c',
        },
        standardSuccess: {
          backgroundColor: '#eaf7eb',
          color: '#1b5e20',
        }
      }
    }
  },
});

// Apply responsive font sizes
theme = responsiveFontSizes(theme);

export default theme;